import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Banknote,
  Bike,
  CalendarClock,
  MapPinned,
  ShieldCheck,
  Store,
  CreditCard,
} from "lucide-react";
import { PageHeading, PageShell } from "@/components/layout/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  AddressForm,
  emptyDraft,
  isDraftValid,
  useDeliveryCheck,
  type AddressDraft,
} from "@/components/order-type/AddressForm";
import { CartTotals } from "@/components/cart/CartPanel";
import { CartLineRow } from "@/components/cart/CartLineRow";
import { EmptyState, Spinner } from "@/components/states";
import { useI18n } from "@/lib/i18n";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import { branchesQuery } from "@/lib/queries";
import { storefrontSdk } from "@/lib/sdk";
import { browserUuid } from "@/lib/browser-ids";
import {
  featureEnabled,
  storefrontOrderingPaused,
  useStorefront,
} from "@/lib/storefront";
import { hasLegalText } from "@/lib/legal";
import { loc } from "@/lib/format";
import type { Address, OrderTypeSelection } from "@/lib/types";
import { cn } from "@/lib/utils";
import { validPickupTime } from "@/lib/checkout-model";
import {
  customerErrorMessage,
  customerErrorUsesDialog,
} from "@/lib/customer-error";
import { useCustomerErrorViewer } from "@/components/customer-error-viewer";
import {
  checkoutDeliveryDetails,
  checkoutDraftKey,
  deliverySelectionKey,
  readCheckoutDraft,
  removeCheckoutDraft,
  writeCheckoutDraft,
} from "@/lib/checkout-draft";
import { writePaymentReturnContext } from "@/lib/payment-return-context";
import { branchOrderBlock, nextScheduledPickupTime } from "@/lib/branch-ordering";
import { BranchOrderNotice } from "@/components/BranchOrderNotice";
import {
  readUnfinishedCheckoutCart,
  unfinishedCheckoutCartKey,
  writeUnfinishedCheckoutCart,
} from "@/lib/unfinished-checkout-cart";

export const Route = createFileRoute("/checkout")({
  component: Checkout,
});

const plusMinutes = (minutes: number) =>
  new Date(Date.now() + minutes * 60_000);

const toLocalInput = (date: Date) => {
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

function Checkout() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const bootstrap = useStorefront();
  const orderingPaused = storefrontOrderingPaused(bootstrap);
  const { showError } = useCustomerErrorViewer();
  const {
    cart,
    hydrated: cartHydrated,
    lineCount,
    quote,
    quotePending,
    setQuantity,
    removeLine,
    refreshQuote,
    clear,
  } = useCart();
  const {
    user,
    isAuthenticated,
    hydrated: authHydrated,
    openLogin,
    upsertAddress,
  } = useAuth();
  const { data: branches } = useQuery(branchesQuery());
  const orderType = cart.orderType;
  const branch = branches?.find(
    (candidate) => candidate.id === orderType?.branchId,
  );
  const initialLocation =
    orderType?.deliveryLocation ??
    (orderType?.deliveryAddress
      ? {
          lat: orderType.deliveryAddress.lat,
          lng: orderType.deliveryAddress.lng,
        }
      : { lat: 0, lng: 0 });
  const [deliveryDraft, setDeliveryDraft] = useState<AddressDraft>(() => ({
    ...emptyDraft(),
    ...initialLocation,
  }));
  const [savedAddressId, setSavedAddressId] = useState<string | null>(
    orderType?.deliveryAddress?.id ?? null,
  );
  const [recipient, setRecipient] = useState(orderType?.recipientName ?? "");
  const [pickupMode, setPickupMode] = useState<"asap" | "scheduled">(
    orderType?.fulfillment.mode ?? "asap",
  );
  const [pickupTime, setPickupTime] = useState(() =>
    orderType?.fulfillment.mode === "scheduled"
      ? toLocalInput(new Date(orderType.fulfillment.scheduledFor))
      : toLocalInput(plusMinutes(1)),
  );
  const [submitting, setSubmitting] = useState(false);
  const [paymentRedirectUrl, setPaymentRedirectUrl] = useState<string | null>(
    null,
  );
  const [orderNotes, setOrderNotes] = useState("");
  const [showValidation, setShowValidation] = useState(false);
  const [draftRestoredFor, setDraftRestoredFor] = useState<string | null>(null);
  const detailsRef = useRef<HTMLElement>(null);
  const submitLockRef = useRef(false);
  const verificationPromptedRef = useRef(false);

  useEffect(() => {
    if (!paymentRedirectUrl) return;
    // Let the handoff screen paint before leaving the storefront.
    let redirected = false;
    const redirect = () => {
      if (redirected) return;
      redirected = true;
      window.clearTimeout(fallbackTimer);
      window.location.assign(paymentRedirectUrl);
    };
    const fallbackTimer = window.setTimeout(redirect, 500);
    let nextFrame = 0;
    const firstFrame = window.requestAnimationFrame(() => {
      nextFrame = window.requestAnimationFrame(redirect);
    });
    return () => {
      window.clearTimeout(fallbackTimer);
      window.cancelAnimationFrame(firstFrame);
      window.cancelAnimationFrame(nextFrame);
    };
  }, [paymentRedirectUrl]);

  const draftKey =
    user?.id && orderType
      ? checkoutDraftKey(
          bootstrap.tenant.id,
          user.id,
          orderType.type,
          orderType.branchId,
        )
      : null;
  const selectedDeliveryKey = deliverySelectionKey(orderType);
  const draftIdentity = draftKey
    ? JSON.stringify([draftKey, selectedDeliveryKey])
    : null;

  const cashAvailable =
    orderType?.type === "delivery"
      ? featureEnabled(bootstrap, "cash_on_delivery")
      : featureEnabled(bootstrap, "cash_on_pickup");
  const onlineAvailable = bootstrap.settings?.paymentMethods.online === true;
  const [paymentMethod, setPaymentMethod] = useState<"cash" | "online">(() =>
    cashAvailable ? "cash" : "online",
  );
  const [onlinePaymentIdempotencyKey, setOnlinePaymentIdempotencyKey] =
    useState<string>(browserUuid);
  const accountsAvailable =
    featureEnabled(bootstrap, "customer_accounts") &&
    featureEnabled(bootstrap, "phone_verification");

  useEffect(() => {
    if (
      verificationPromptedRef.current ||
      !cartHydrated ||
      !authHydrated ||
      lineCount === 0 ||
      !accountsAvailable ||
      isAuthenticated
    )
      return;
    verificationPromptedRef.current = true;
    openLogin();
  }, [
    accountsAvailable,
    authHydrated,
    cartHydrated,
    isAuthenticated,
    lineCount,
    openLogin,
  ]);
  const pickupAsapAvailable = featureEnabled(bootstrap, "asap_ordering");
  const pickupScheduledAvailable = featureEnabled(
    bootstrap,
    "scheduled_pickup",
  );
  const deliveryCheck = useDeliveryCheck(
    { lat: deliveryDraft.lat, lng: deliveryDraft.lng },
    orderType?.type === "delivery",
  );
  const termsAvailable = hasLegalText(bootstrap, "terms");

  useEffect(() => {
    if (paymentMethod === "cash" && !cashAvailable && onlineAvailable)
      setPaymentMethod("online");
    if (paymentMethod === "online" && !onlineAvailable && cashAvailable)
      setPaymentMethod("cash");
  }, [cashAvailable, onlineAvailable, paymentMethod]);

  useEffect(() => {
    if (!orderType || orderType.type !== "pickup") return;
    setPickupMode(orderType.fulfillment.mode);
    if (orderType.fulfillment.mode === "scheduled") {
      setPickupTime(toLocalInput(new Date(orderType.fulfillment.scheduledFor)));
    }
  }, [orderType]);

  useEffect(() => {
    if (
      pickupMode === "asap" &&
      !pickupAsapAvailable &&
      pickupScheduledAvailable
    ) {
      setPickupMode("scheduled");
      setPickupTime(toLocalInput(plusMinutes(1)));
    }
    if (
      pickupMode === "scheduled" &&
      !pickupScheduledAvailable &&
      pickupAsapAvailable
    ) {
      setPickupMode("asap");
    }
  }, [pickupAsapAvailable, pickupMode, pickupScheduledAvailable]);

  useEffect(() => {
    if (
      !cartHydrated ||
      !authHydrated ||
      !draftKey ||
      draftRestoredFor === draftIdentity
    )
      return;
    const saved = readCheckoutDraft(window.sessionStorage, draftKey);
    if (orderType?.type === "delivery") {
      const details = checkoutDeliveryDetails(orderType, user, saved);
      setDeliveryDraft(details.deliveryDraft);
      setSavedAddressId(details.savedAddressId);
    }
    if (saved) {
      setRecipient(saved.recipient || user?.name || "");
      setPickupMode(saved.pickupMode);
      setPickupTime(saved.pickupTime);
      setPaymentMethod(saved.paymentMethod);
      setOnlinePaymentIdempotencyKey(saved.onlinePaymentIdempotencyKey);
      setOrderNotes(saved.orderNotes);
    } else {
      setRecipient(orderType?.recipientName || user?.name || "");
    }
    setDraftRestoredFor(draftIdentity);
  }, [
    authHydrated,
    cartHydrated,
    draftKey,
    draftIdentity,
    draftRestoredFor,
    orderType,
    user,
  ]);

  useEffect(() => {
    if (!draftKey || draftRestoredFor !== draftIdentity) return;
    writeCheckoutDraft(window.sessionStorage, draftKey, {
      deliveryDraft,
      savedAddressId,
      deliverySelectionKey: selectedDeliveryKey,
      recipient,
      pickupMode,
      pickupTime,
      paymentMethod,
      onlinePaymentIdempotencyKey,
      orderNotes,
    });
  }, [
    deliveryDraft,
    draftKey,
    draftIdentity,
    draftRestoredFor,
    orderNotes,
    pickupMode,
    pickupTime,
    paymentMethod,
    onlinePaymentIdempotencyKey,
    recipient,
    savedAddressId,
    selectedDeliveryKey,
  ]);

  useEffect(() => {
    if (cartHydrated && lineCount > 0 && !orderType) {
      navigate({ to: "/order-type", replace: true });
    }
  }, [cartHydrated, lineCount, navigate, orderType]);

  const deliveryAddress = useMemo<Address | null>(() => {
    if (!orderType || orderType.type !== "delivery") return null;
    const savedAddress = savedAddressId
      ? user?.addresses.find((address) => address.id === savedAddressId)
      : undefined;
    return {
      id: savedAddressId ?? "new",
      label: savedAddress?.label ?? { en: "Home", ar: "المنزل" },
      fullName: deliveryDraft.fullName.trim(),
      phone: user?.phone ?? deliveryDraft.phone,
      street: deliveryDraft.street.trim(),
      area: deliveryDraft.area.trim(),
      building: deliveryDraft.building.trim(),
      floor: deliveryDraft.floor.trim(),
      apartment: deliveryDraft.apartment.trim(),
      landmark: deliveryDraft.landmark.trim(),
      ...(deliveryDraft.note.trim() ? { note: deliveryDraft.note.trim() } : {}),
      lat: deliveryDraft.lat,
      lng: deliveryDraft.lng,
    };
  }, [deliveryDraft, orderType, savedAddressId, user?.addresses, user?.phone]);

  const pickupScheduledFor =
    pickupMode === "scheduled" ? new Date(pickupTime) : null;
  const pickupTimeValid = pickupMode === "asap" || validPickupTime(pickupTime);
  const detailsValid =
    orderType?.type === "delivery"
      ? !deliveryCheck.isFetching &&
        isDraftValid(deliveryDraft, deliveryCheck.data)
      : recipient.trim().length >= 2 && pickupTimeValid;

  function selectionForCheckout(): OrderTypeSelection | null {
    if (!orderType) return null;
    if (orderType.type === "delivery" && deliveryAddress) {
      return {
        ...orderType,
        fulfillment: { mode: "asap" },
        deliveryLocation: {
          lat: deliveryAddress.lat,
          lng: deliveryAddress.lng,
        },
        deliveryAddress,
      };
    }
    if (orderType.type === "pickup") {
      const scheduledFor = pickupScheduledFor?.toISOString();
      return {
        ...orderType,
        fulfillment:
          pickupMode === "scheduled" && scheduledFor
            ? { mode: "scheduled", scheduledFor }
            : { mode: "asap" },
        recipientName: recipient.trim(),
        ...(scheduledFor ? { pickupTimeISO: scheduledFor } : {}),
      };
    }
    return orderType;
  }

  const checkoutBlock = orderingPaused
    ? "global" as const
    : branchOrderBlock(branch, selectionForCheckout()?.fulfillment);

  async function submit() {
    if (
      submitLockRef.current ||
      submitting ||
      quotePending ||
      bootstrap.mode === "preview"
    )
      return;
    if (orderingPaused) {
      showError({ code: "ordering_paused" });
      return;
    }
    if (checkoutBlock) {
      toast.error(t("ordering.unavailable"));
      return;
    }
    if (!accountsAvailable) {
      toast.error(t("checkout.accountUnavailable"));
      return;
    }
    if (!isAuthenticated || !user?.verified || user.name.trim().length < 2) {
      openLogin();
      return;
    }
    if (!detailsValid) {
      setShowValidation(true);
      toast.error(t("orderType.required"));
      window.requestAnimationFrame(() => {
        detailsRef.current
          ?.querySelector<HTMLElement>('[aria-invalid="true"]')
          ?.focus();
      });
      return;
    }
    const checkoutSelection = selectionForCheckout();
    if (!checkoutSelection) {
      navigate({ to: "/order-type" });
      return;
    }
    submitLockRef.current = true;
    setSubmitting(true);
    try {
      if (checkoutSelection.type === "delivery" && deliveryAddress) {
        await upsertAddress(deliveryAddress);
      }
      const currentQuote = await refreshQuote({
        ...cart,
        orderType: checkoutSelection,
      });
      const address =
        checkoutSelection.type === "delivery" && deliveryAddress
          ? {
              street: deliveryAddress.street,
              area: deliveryAddress.area,
              building: deliveryAddress.building,
              floor: deliveryAddress.floor,
              apartment: deliveryAddress.apartment,
              landmark: deliveryAddress.landmark,
              instructions: deliveryAddress.note ?? "",
              lat: deliveryAddress.lat,
              lng: deliveryAddress.lng,
            }
          : undefined;
      const commonOrder = {
        quoteId: currentQuote.quoteId,
        locale: lang,
        contact: {
          name:
            checkoutSelection.type === "delivery"
              ? deliveryAddress?.fullName || user.name
              : recipient.trim() || user.name,
          phone: user.phone,
        },
        ...(address ? { address } : {}),
        ...(orderNotes.trim() ? { notes: orderNotes.trim() } : {}),
      };
      const result = await storefrontSdk.createOrder(
        paymentMethod === "online"
          ? {
              ...commonOrder,
              paymentMethod: "online",
              idempotencyKey: onlinePaymentIdempotencyKey,
            }
          : {
              ...commonOrder,
              paymentMethod:
                checkoutSelection.type === "delivery"
                  ? "cash_delivery"
                  : "cash_pickup",
            },
      );
      void queryClient.invalidateQueries({
        queryKey: ["customer-loyalty", user.id],
      });
      if (draftKey) removeCheckoutDraft(window.sessionStorage, draftKey);
      if ("paymentAttempt" in result) {
        writePaymentReturnContext(window.sessionStorage, {
          attemptId: result.paymentAttempt.id,
          orderId: result.order.id,
          startedAt: new Date().toISOString(),
        });
        writeUnfinishedCheckoutCart(
          window.localStorage,
          unfinishedCheckoutCartKey(bootstrap.tenant.id, user.id),
          {
            attemptId: result.paymentAttempt.id,
            orderId: result.order.id,
            cart: { ...cart, orderType: checkoutSelection },
          },
        );
        if (result.paymentAttempt.nextAction?.type === "redirect") {
          setPaymentRedirectUrl(result.paymentAttempt.nextAction.url);
          clear();
        } else {
          clear();
          navigate({ to: "/payment-return" });
        }
        return;
      }
      clear();
      navigate({ to: "/order-success", search: { orderId: result.id } });
    } catch (error) {
      const message = customerErrorMessage(error, t);
      if (customerErrorUsesDialog(error)) {
        showError(error);
      } else {
        toast.error(message);
      }
    } finally {
      submitLockRef.current = false;
      setSubmitting(false);
    }
  }

  const submitDisabled =
    submitting ||
    bootstrap.mode === "preview" ||
    orderingPaused ||
    Boolean(checkoutBlock) ||
    quotePending ||
    !quote ||
    !accountsAvailable ||
    (paymentMethod === "cash" ? !cashAvailable : !onlineAvailable);
  const submitLabel = submitting
    ? t("checkout.submitting")
    : quotePending
      ? t("cart.calculating")
      : t(
          paymentMethod === "online"
            ? "checkout.continueOnline"
            : "checkout.confirmCash",
        );
  const unfinishedCheckout =
    typeof window !== "undefined" &&
    cartHydrated &&
    authHydrated &&
    lineCount === 0 &&
    user?.id
      ? readUnfinishedCheckoutCart(
          window.localStorage,
          unfinishedCheckoutCartKey(bootstrap.tenant.id, user.id),
        )
      : null;

  if (paymentRedirectUrl) {
    return (
      <PageShell size="narrow">
        <section
          className="surface-card mx-auto grid max-w-lg place-items-center gap-4 p-7 text-center sm:p-10"
          role="status"
          aria-live="polite"
        >
          <span className="grid size-16 place-items-center rounded-full bg-accent text-primary">
            <Spinner className="size-8 motion-reduce:animate-none" />
          </span>
          <div>
            <h1 className="text-xl font-black">{t("checkout.redirectTitle")}</h1>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              {t("checkout.redirectDescription")}
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-2">
            <Button asChild variant="outline">
              <a href={paymentRedirectUrl}>{t("checkout.continueOnline")}</a>
            </Button>
            <Button asChild variant="ghost">
              <Link to="/cart">{t("checkout.returnToCart")}</Link>
            </Button>
          </div>
        </section>
      </PageShell>
    );
  }

  if (
    !cartHydrated ||
    !authHydrated ||
    (lineCount > 0 && !orderType) ||
    (draftIdentity !== null && draftRestoredFor !== draftIdentity)
  ) {
    return (
      <PageShell size="narrow">
        <div
          className="grid min-h-64 place-items-center"
          role="status"
          aria-label={t("common.loading")}
        >
          <Spinner className="size-8" />
        </div>
      </PageShell>
    );
  }

  if (lineCount === 0) {
    return (
      <PageShell size="narrow">
        <EmptyState
          title={t(unfinishedCheckout ? "checkout.savedCartTitle" : "cart.empty")}
          description={t(
            unfinishedCheckout ? "checkout.savedCartDescription" : "cart.emptyDesc",
          )}
          action={
            <Button asChild>
              <Link to={unfinishedCheckout ? "/cart" : "/menu"}>
                {t(unfinishedCheckout ? "checkout.returnToCart" : "cart.browse")}
              </Link>
            </Button>
          }
        />
      </PageShell>
    );
  }

  if (!isAuthenticated || !user?.verified) {
    return (
      <PageShell className="max-w-5xl">
        <PageHeading
          breadcrumbs={[
            { label: t("nav.home"), to: "/" },
            { label: t("cart.title"), to: "/cart" },
            { label: t("checkout.title") },
          ]}
          back
          backTo="/cart"
          title={t("checkout.title")}
          description={t("checkout.subtitle")}
        />
        <section className="surface-card pattern-levant mx-auto max-w-2xl overflow-hidden text-center">
          <div className="grid place-items-center gap-3 px-6 py-10 sm:px-10">
            <span className="grid size-16 place-items-center rounded-full bg-card text-primary shadow-soft">
              <ShieldCheck className="size-8" aria-hidden="true" />
            </span>
            <h2 className="text-xl font-extrabold">
              {t("checkout.verifyTitle")}
            </h2>
            <p className="max-w-md text-sm leading-6 text-muted-foreground">
              {t(
                bootstrap.mode === "preview"
                  ? "checkout.preview"
                  : !accountsAvailable
                    ? "checkout.accountUnavailable"
                    : "checkout.verifyDescription",
              )}
            </p>
          </div>
          <div className="flex flex-col items-center justify-between gap-4 border-t border-border bg-card/80 px-5 py-4 sm:flex-row sm:text-start">
            <div>
              <p className="font-bold">{t("checkout.verifyCartReady")}</p>
              <p className="text-sm text-muted-foreground">
                {t(lineCount === 1 ? "cart.line" : "cart.lines", {
                  count: lineCount,
                })}
              </p>
            </div>
            <Button
              size="lg"
              disabled={!accountsAvailable || bootstrap.mode === "preview"}
              onClick={() => openLogin()}
            >
              {t("checkout.verifyAction")}
            </Button>
          </div>
        </section>
      </PageShell>
    );
  }

  return (
    <PageShell className="pb-8 sm:pb-14">
      <PageHeading
        breadcrumbs={[
          { label: t("nav.home"), to: "/" },
          { label: t("cart.title"), to: "/cart" },
          { label: t("checkout.title") },
        ]}
        back
        backTo="/cart"
        title={t("checkout.title")}
        description={t("checkout.subtitle")}
      />
      <div className="mb-5"><BranchOrderNotice reason={checkoutBlock} /></div>

      <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_340px] xl:gap-7">
        <div className="grid content-start gap-5">
          <section ref={detailsRef} className="surface-card overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border bg-accent/35 px-4 py-4 sm:px-5">
              <div className="flex items-center gap-3">
                <span className="grid size-11 place-items-center rounded-2xl bg-primary text-primary-foreground">
                  {orderType?.type === "delivery" ? (
                    <Bike className="size-5" />
                  ) : (
                    <Store className="size-5" />
                  )}
                </span>
                <div>
                  <h2 className="text-lg font-bold">
                    {t("checkout.fulfillment")}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {orderType?.type === "delivery"
                      ? t("menu.delivery")
                      : t("menu.pickup")}
                    {branch ? ` — ${loc(branch.name, lang)}` : ""}
                  </p>
                </div>
              </div>
              <Button asChild size="sm" variant="outline">
                <Link to="/order-type" search={{ returnTo: "/checkout" }}>
                  {t("checkout.changeType")}
                </Link>
              </Button>
            </div>

            <div className="grid gap-5 p-4 sm:p-5">
              <div className="flex items-center gap-3 rounded-xl border border-success/25 bg-success/8 px-3 py-2.5 text-sm">
                <ShieldCheck
                  className="size-5 shrink-0 text-success"
                  aria-hidden="true"
                />
                <div>
                  <p className="font-bold">{t("checkout.verifiedPhone")}</p>
                  <p className="text-sm font-medium">{user.name}</p>
                  <p className="text-muted-foreground" dir="ltr">
                    {user.phone}
                  </p>
                </div>
              </div>

              {orderType?.type === "delivery" ? (
                <>
                  <div className="flex items-start justify-between gap-3 rounded-2xl border border-border bg-muted/45 p-4">
                    <div className="flex items-start gap-3">
                      <MapPinned
                        className="mt-0.5 size-5 shrink-0 text-primary"
                        aria-hidden="true"
                      />
                      <div>
                        <p className="font-bold">
                          {t("checkout.deliveryPoint")}
                        </p>
                        <p className="mt-1 text-sm text-muted-foreground">
                          {t("checkout.deliveryPointDescription")}
                        </p>
                      </div>
                    </div>
                    <Button asChild size="sm" variant="ghost">
                      <Link to="/order-type" search={{ returnTo: "/checkout" }}>
                        {t("common.change")}
                      </Link>
                    </Button>
                  </div>

                  <AddressForm
                    draft={deliveryDraft}
                    onChange={(next) => {
                      setSavedAddressId(null);
                      setDeliveryDraft({ ...next, phone: user.phone });
                    }}
                    showMap={false}
                    phoneReadOnly
                    compact
                    hideContact
                    showErrors={showValidation}
                  />
                  <DeliveryValidationState
                    isFetching={deliveryCheck.isFetching}
                    isError={deliveryCheck.isError}
                    deliverable={deliveryCheck.data?.deliverable}
                    onRetry={() => deliveryCheck.refetch()}
                  />
                </>
              ) : (
                <div className="grid gap-5">
                  <div className="grid gap-1.5">
                    <Label htmlFor="pickup-name">
                      {t("orderType.recipient")} *
                    </Label>
                    <Input
                      id="pickup-name"
                      autoComplete="name"
                      minLength={2}
                      value={recipient}
                      aria-invalid={
                        showValidation && recipient.trim().length < 2
                      }
                      aria-describedby={
                        showValidation && recipient.trim().length < 2
                          ? "pickup-name-error"
                          : undefined
                      }
                      onChange={(event) => setRecipient(event.target.value)}
                      required
                    />
                    {showValidation && recipient.trim().length < 2 ? (
                      <p
                        id="pickup-name-error"
                        className="text-xs font-medium text-destructive"
                      >
                        {t("orderType.nameRequired")}
                      </p>
                    ) : null}
                  </div>

                  <div className="grid gap-3">
                    <p className="text-sm font-bold">
                      {t("orderType.pickupTime")}
                    </p>
                    <div className="grid gap-3 sm:grid-cols-2">
                      {pickupAsapAvailable ? (
                        <button
                          type="button"
                          aria-pressed={pickupMode === "asap"}
                          onClick={() => setPickupMode("asap")}
                          className={cn(
                            "pressable flex min-h-14 items-center gap-3 rounded-2xl border p-4 text-start",
                            pickupMode === "asap"
                              ? "border-primary bg-accent shadow-soft"
                              : "border-border hover:bg-accent/60",
                          )}
                        >
                          <Bike
                            className="size-5 text-primary"
                            aria-hidden="true"
                          />
                          <span className="font-bold">
                            {t("orderType.asap")}
                          </span>
                        </button>
                      ) : null}
                      {pickupScheduledAvailable ? (
                        <button
                          type="button"
                          aria-pressed={pickupMode === "scheduled"}
                          onClick={() => {
                            setPickupMode("scheduled");
                            setPickupTime(
                              toLocalInput(
                                (branch && nextScheduledPickupTime(branch)) ?? plusMinutes(15),
                              ),
                            );
                          }}
                          className={cn(
                            "pressable flex min-h-14 items-center gap-3 rounded-2xl border p-4 text-start",
                            pickupMode === "scheduled"
                              ? "border-primary bg-accent shadow-soft"
                              : "border-border hover:bg-accent/60",
                          )}
                        >
                          <CalendarClock
                            className="size-5 text-primary"
                            aria-hidden="true"
                          />
                          <span className="font-bold">
                            {t("orderType.scheduled")}
                          </span>
                        </button>
                      ) : null}
                    </div>
                    {pickupMode === "scheduled" ? (
                      <div className="grid gap-1.5">
                        <Label htmlFor="pickup-time">
                          {t("orderType.pickupTime")}
                        </Label>
                        <Input
                          id="pickup-time"
                          aria-describedby="pickup-time-hint"
                          type="datetime-local"
                          value={pickupTime}
                          min={toLocalInput(plusMinutes(1))}
                          max={toLocalInput(plusMinutes(7 * 24 * 60))}
                          onChange={(event) =>
                            setPickupTime(event.target.value)
                          }
                          aria-invalid={!pickupTimeValid}
                        />
                        <p
                          id="pickup-time-hint"
                          className={cn(
                            "text-xs",
                            pickupTimeValid
                              ? "text-muted-foreground"
                              : "font-medium text-destructive",
                          )}
                        >
                          {t(
                            pickupTimeValid
                              ? "orderType.pickupHint"
                              : "checkout.invalidPickupTime",
                          )}
                        </p>
                      </div>
                    ) : null}
                  </div>
                </div>
              )}
            </div>
          </section>

          <section className="surface-card overflow-hidden">
            <div className="p-4 sm:p-5">
              <div className="mb-4">
                <h2 className="text-lg font-bold">{t("checkout.payment")}</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  {t("checkout.paymentHint")}
                </p>
              </div>
              {cashAvailable || onlineAvailable ? (
                <div
                  className="grid gap-3 sm:grid-cols-2"
                  role="radiogroup"
                  aria-label={t("checkout.payment")}
                >
                  {cashAvailable ? (
                    <PaymentChoice
                      value="cash"
                      selected={paymentMethod === "cash"}
                      icon={<Banknote className="size-5" aria-hidden="true" />}
                      label={t(
                        orderType?.type === "pickup"
                          ? "checkout.cashPickup"
                          : "checkout.cash",
                      )}
                      description={t("checkout.cashDescription")}
                      onSelect={() => setPaymentMethod("cash")}
                    />
                  ) : null}
                  {onlineAvailable ? (
                    <PaymentChoice
                      value="online"
                      selected={paymentMethod === "online"}
                      icon={
                        <CreditCard className="size-5" aria-hidden="true" />
                      }
                      label={t("checkout.onlineCard")}
                      description={t("checkout.onlineCardDescription")}
                      onSelect={() => setPaymentMethod("online")}
                    />
                  ) : null}
                </div>
              ) : (
                <p className="mt-3 text-sm font-medium text-destructive">
                  {t("checkout.noPaymentMethods")}
                </p>
              )}
              {!accountsAvailable ? (
                <p className="mt-3 text-sm font-medium text-destructive">
                  {t("checkout.accountUnavailable")}
                </p>
              ) : null}
            </div>

            {featureEnabled(bootstrap, "order_notes") ? (
              <details className="group border-t border-border bg-muted/20">
                <summary className="cursor-pointer list-none px-4 py-4 text-sm font-bold marker:hidden sm:px-5">
                  {t("checkout.addNotes")}
                  <span
                    className="ms-2 inline-block text-muted-foreground transition-transform group-open:rotate-180"
                    aria-hidden="true"
                  >
                    ⌄
                  </span>
                </summary>
                <div className="border-t border-border p-4 sm:p-5">
                  <p className="mb-3 text-sm text-muted-foreground">
                    {t("checkout.notesHint")}
                  </p>
                  <Textarea
                    aria-label={t("checkout.notes")}
                    value={orderNotes}
                    onChange={(event) => setOrderNotes(event.target.value)}
                    maxLength={1000}
                    placeholder={t("common.notePlaceholder")}
                    className="min-h-24 bg-card"
                  />
                </div>
              </details>
            ) : null}
          </section>
        </div>

        <aside className="surface-card grid h-fit content-start gap-5 p-4 sm:p-5 lg:sticky lg:top-24">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-lg font-bold">{t("checkout.summary")}</h2>
            <span className="rounded-full bg-muted px-2.5 py-1 text-xs font-bold text-muted-foreground">
              {t(lineCount === 1 ? "cart.line" : "cart.lines", {
                count: lineCount,
              })}
            </span>
          </div>
          <div className="grid gap-3">
            {cart.lines.map((line) => (
              <CartLineRow
                key={line.id}
                line={line}
                card
                onQuantity={(quantity) => setQuantity(line.id, quantity)}
                onRemove={() => removeLine(line.id)}
              />
            ))}
          </div>
          <CartTotals comfortable />
          {showValidation && !detailsValid ? (
            <p className="rounded-xl bg-accent/60 p-3 text-sm" role="status">
              {t(
                orderType?.type === "pickup" && !pickupTimeValid
                  ? "checkout.invalidPickupTime"
                  : "checkout.completeDetails",
              )}
            </p>
          ) : null}
          {bootstrap.mode === "preview" ? (
            <p className="text-sm text-muted-foreground">
              {t("checkout.preview")}
            </p>
          ) : null}
          <Button
            size="lg"
            className="w-full"
            disabled={submitDisabled}
            onClick={submit}
          >
            {submitting || quotePending ? <Spinner className="size-4" /> : null}
            {submitLabel}
          </Button>
          {termsAvailable ? (
            <p className="text-xs leading-5 text-muted-foreground">
              {t("checkout.legal")}{" "}
              <Link
                to="/terms-and-conditions"
                className="font-bold text-primary hover:underline"
              >
                {t("legal.terms")}
              </Link>
            </p>
          ) : null}
        </aside>
      </div>
    </PageShell>
  );
}

function PaymentChoice({
  value,
  selected,
  icon,
  label,
  description,
  onSelect,
}: {
  value: "cash" | "online";
  selected: boolean;
  icon: React.ReactNode;
  label: string;
  description: string;
  onSelect: () => void;
}) {
  return (
    <label
      className={cn(
        "pressable flex min-h-24 cursor-pointer items-start gap-3 rounded-2xl border p-4 text-start focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
        selected
          ? "border-primary bg-accent shadow-soft"
          : "border-border hover:bg-accent/60",
      )}
    >
      <input
        type="radio"
        name="payment-method"
        value={value}
        checked={selected}
        onChange={onSelect}
        className="sr-only"
      />
      <span className="mt-0.5 text-primary">{icon}</span>
      <span>
        <span className="block font-bold">{label}</span>
        <span className="mt-1 block text-xs leading-5 text-muted-foreground">
          {description}
        </span>
      </span>
    </label>
  );
}

function DeliveryValidationState({
  isFetching,
  isError,
  deliverable,
  onRetry,
}: {
  isFetching: boolean;
  isError: boolean;
  deliverable: boolean | undefined;
  onRetry: () => unknown;
}) {
  const { t } = useI18n();
  if (isFetching) {
    return (
      <p className="text-sm text-muted-foreground" role="status">
        {t("orderType.checking")}
      </p>
    );
  }
  if (isError) {
    return (
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => void onRetry()}
      >
        {t("orderType.checkError")}
      </Button>
    );
  }
  if (deliverable === false) {
    return (
      <p
        className="rounded-xl bg-destructive/10 p-3 text-sm font-medium text-destructive"
        role="alert"
      >
        {t("orderType.zoneUnavailable")}
      </p>
    );
  }
  return null;
}
